import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { doc, getDoc, collection, query, where, limit, getDocs, addDoc, serverTimestamp, orderBy } from "firebase/firestore";
import { db } from "../firebase/config";
import { FiChevronDown, FiChevronUp, FiStar, FiSend } from "react-icons/fi";

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState("description");

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const snap = await getDoc(doc(db, "products", id));
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setProduct(data);
          setSelectedImage(data.mainImage || (data.images && data.images[0]));

          if (data.category) {
            const relQ = query(
              collection(db, "products"),
              where("category", "==", data.category),
              limit(5)
            );
            const relSnap = await getDocs(relQ);
            setRelated(relSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((p) => p.id !== id));
          }
        }
      } catch (e) {}
      setLoading(false);
    };
    fetchProduct();
  }, [id]);

  const toggleSection = (name) => {
    setOpenSection((prev) => (prev === name ? null : name));
  };

  // ── Reviews ──
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ name: "", email: "", comment: "" });
  const [reviewRating, setReviewRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchReviews = async () => {
      try {
        const q = query(collection(db, "reviews"), where("productId", "==", id), orderBy("createdAt", "desc"));
        const snap = await getDocs(q);
        setReviews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {}
    };
    fetchReviews();
  }, [id]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!reviewForm.name || !reviewForm.email || !reviewRating || !reviewForm.comment) return;
    setSubmitting(true);
    try {
      await addDoc(collection(db, "reviews"), {
        productId: id,
        productTitle: product?.title || "",
        ...reviewForm,
        rating: reviewRating,
        createdAt: serverTimestamp(),
      });
      setReviewForm({ name: "", email: "", comment: "" });
      setReviewRating(0);
      const q = query(collection(db, "reviews"), where("productId", "==", id), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setReviews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { alert("Failed to submit review: " + e.message); }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <p className="text-xl text-gray-400 mb-4">Product not found.</p>
        <Link to="/shop" className="text-[#e84fa5] hover:underline">← Back to Shop</Link>
      </div>
    );
  }

  const allImages = [
    ...(product.mainImage ? [product.mainImage] : []),
    ...(product.images || []).filter((img) => img !== product.mainImage),
  ];

  return (
    <div className="bg-white min-h-screen" style={{ fontFamily: "Montserrat, sans-serif" }}>
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12 py-10">
        {/* Breadcrumb */}
        <div className="text-[12px] text-gray-400 mb-8 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-[#e84fa5] transition">Home</Link>
          <span>/</span>
          <Link to="/shop" className="hover:text-[#e84fa5] transition">Shop</Link>
          <span>/</span>
          <span className="text-gray-700">{product.category || "Product"}</span>
        </div>

        {/* Main Product Section */}
        <div className="grid lg:grid-cols-2 gap-10 mb-16">
          {/* LEFT - Images */}
          <div>
            <div className="w-full aspect-square bg-gray-50 rounded-xl overflow-hidden border border-gray-100 mb-4">
              <img
                src={selectedImage || "/placeholder.png"}
                alt={product.title}
                className="w-full h-full object-cover"
              />
            </div>

            {allImages.length > 1 && (
              <div className="flex gap-3 flex-wrap">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(img)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition ${
                      selectedImage === img ? "border-[#e84fa5]" : "border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    <img src={img} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT - Info */}
          <div>
            {product.category && (
              <span className="inline-block px-3 py-1 bg-pink-50 text-[#e84fa5] text-[11px] font-bold uppercase rounded-full mb-3">
                {product.category}
              </span>
            )}

            <h1 className="text-2xl md:text-3xl font-extrabold text-black leading-tight mb-4">
              {product.title}
            </h1>

            {/* Stars & Reviews */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center text-[#f5a623] text-[13px]">
                {[1, 2, 3, 4, 5].map((s) => (
                  <FiStar key={s} fill={s <= (reviews.length ? Math.round(reviews.reduce((a, r) => a + r.rating, 0) / reviews.length) : 0) ? "#f5a623" : "none"} stroke="#f5a623" />
                ))}
              </div>
              {reviews.length > 0 && <span className="text-[12px] text-gray-400">({reviews.length} Review{reviews.length !== 1 ? "s" : ""})</span>}
            </div>

            {/* Price */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl font-extrabold text-black">${product.price}</span>
            </div>

            {/* About This Item */}
            {product.aboutItem && (
              <p className="text-[13px] text-gray-600 leading-relaxed mb-6">{product.aboutItem}</p>
            )}

            {/* Style / Size / ItemCode - only show if filled */}
            {(product.style || product.size || product.itemCode) && (
              <div className="space-y-3 mb-6 text-[13px] text-gray-600">
                {product.style && (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800 w-24">Style:</span>
                    <span>{product.style}</span>
                  </div>
                )}
                {product.size && (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800 w-24">Size:</span>
                    <span>{product.size}</span>
                  </div>
                )}
                {product.itemCode && (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800 w-24">Item Code:</span>
                    <span>{product.itemCode}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800 w-24">Category:</span>
                  <span className="text-[#e84fa5] font-semibold">{product.category || "NA"}</span>
                </div>
              </div>
            )}

            {/* Buy Now Button */}
            {(product.buyLink || product.buyLink2) && (
              <a
                href={product.buyLink || product.buyLink2}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full text-center px-10 py-4 bg-black text-white font-bold uppercase text-[13px] hover:bg-gray-800 transition rounded mb-4"
              >
                Buy Now
              </a>
            )}

            {product.buyLink && product.buyLink2 && (
              <p className="text-[11px] text-gray-400 text-center">
                Also available on{" "}
                <a href={product.buyLink2} target="_blank" rel="noopener noreferrer" className="text-[#e84fa5] hover:underline">
                  second platform
                </a>
              </p>
            )}
          </div>
        </div>

        {/* Accordion Sections */}
        <div className="border-t border-gray-200 mb-16">
          {/* Description */}
          <div className="border-b border-gray-200">
            <button onClick={() => toggleSection("description")}
              className="w-full flex items-center justify-between py-5 text-[14px] font-extrabold uppercase tracking-wide text-black">
              Description
              {openSection === "description" ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
            </button>
            {openSection === "description" && (
              <div className="pb-6 text-[13px] text-gray-600 leading-relaxed">
                {product.description || "No description available."}
                {product.info && (
                  <div className="mt-4">
                    <p className="font-semibold text-black mb-2">Product Details:</p>
                    <p className="whitespace-pre-line">{product.info}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Information */}
          <div className="border-b border-gray-200">
            <button onClick={() => toggleSection("information")}
              className="w-full flex items-center justify-between py-5 text-[14px] font-extrabold uppercase tracking-wide text-black">
              Information
              {openSection === "information" ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
            </button>
            {openSection === "information" && (
              <div className="pb-6 text-[13px] text-gray-600 leading-relaxed">
                {product.info ? (
                  <p className="whitespace-pre-line">{product.info}</p>
                ) : (
                  <p className="text-gray-400">No additional information available.</p>
                )}
                {product.platform && (
                  <p className="mt-3 text-[12px] text-gray-500">
                    <span className="font-semibold">Available on:</span> {product.platform}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Reviews */}
          <div className="border-b border-gray-200">
            <button onClick={() => toggleSection("reviews")}
              className="w-full flex items-center justify-between py-5 text-[14px] font-extrabold uppercase tracking-wide text-black">
              Reviews
              {openSection === "reviews" ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
            </button>
            {openSection === "reviews" && (
              <div className="pb-6">
                {/* Existing Reviews */}
                {reviews.length > 0 ? (
                  <div className="space-y-4 mb-8">
                    {reviews.map((r) => (
                      <div key={r.id} className="border-b border-gray-100 pb-4">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[13px] font-bold text-black">{r.name}</span>
                          <span className="text-[11px] text-gray-400">-</span>
                          <span className="text-[11px] text-gray-400">{r.createdAt?.toDate?.().toLocaleDateString() || ""}</span>
                        </div>
                        <div className="flex items-center text-[#f5a623] text-[12px] mb-2">
                          {[1, 2, 3, 4, 5].map((s) => (
                            <FiStar key={s} fill={s <= r.rating ? "#f5a623" : "none"} stroke="#f5a623" />
                          ))}
                        </div>
                        <p className="text-[13px] text-gray-600">{r.comment}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-[13px] text-gray-500 mb-6">No reviews yet. Be the first to review this product!</div>
                )}

                {/* Write a Review */}
                <form onSubmit={handleReviewSubmit} className="bg-gray-50 rounded-xl p-6">
                  <h4 className="text-[13px] font-bold mb-4">Write a Review</h4>
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button key={star} type="button" onClick={() => setReviewRating(star)}
                        className="text-[#f5a623] text-[22px] hover:scale-110 transition">
                        <FiStar fill={star <= reviewRating ? "#f5a623" : "none"} stroke="#f5a623" />
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <input value={reviewForm.name} onChange={(e) => setReviewForm((s) => ({ ...s, name: e.target.value }))}
                      placeholder="Name*" required
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
                    <input value={reviewForm.email} onChange={(e) => setReviewForm((s) => ({ ...s, email: e.target.value }))} type="email"
                      placeholder="Email*" required
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
                  </div>
                  <textarea value={reviewForm.comment} onChange={(e) => setReviewForm((s) => ({ ...s, comment: e.target.value }))}
                    rows={3} placeholder="Your review*" required
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-[13px] outline-none focus:border-[#e84fa5] resize-none mb-3" />
                  <button type="submit" disabled={submitting}
                    className="flex items-center gap-2 px-6 py-2.5 bg-black text-white text-[11px] font-bold uppercase rounded hover:bg-gray-800 transition disabled:opacity-50">
                    <FiSend size={12} /> {submitting ? "Submitting..." : "Submit Review"}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div>
            <h2 className="text-[18px] font-extrabold uppercase tracking-wide mb-6">Related Products</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {related.slice(0, 4).map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="group">
                  <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-100 hover:shadow-md transition">
                    <div className="aspect-square overflow-hidden">
                      <img
                        src={p.mainImage || (p.images && p.images[0]) || "/placeholder.png"}
                        alt={p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="text-[12px] font-bold text-black leading-tight mb-1 line-clamp-2">{p.title}</h3>
                      <p className="text-[14px] font-extrabold text-black">${p.price}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
